import {AvailableApp} from "services/loginServices";
import {AppNames} from "enums";

export type AppLinkEnvs = { prod: boolean; test: boolean }

/** Apps that can be toggled in admin access UI (excludes 0 Unknown). */
export const ADMIN_CONFIGURABLE_APP_IDS = [1, 2, 6] as const

export const groupAppLinksByEnv = (links: AvailableApp[] | undefined) => {
    const byApp = new Map<number, AppLinkEnvs>()
    if (!links?.length) {
        return byApp
    }
    for (const al of links) {
        const cur = byApp.get(al.appId) ?? {prod: false, test: false}
        if (al.isTest) {
            cur.test = true
        } else {
            cur.prod = true
        }
        byApp.set(al.appId, cur)
    }
    return byApp
}

export const appLinksSortValue = (links: AvailableApp[] | undefined) => {
    const m = groupAppLinksByEnv(links)
    if (!m.size) {
        return ""
    }
    return [...m.entries()]
        .sort(([a], [b]) => a - b)
        .map(([appId, env]) => {
            const parts = [env.prod ? "Prod" : null, env.test ? "Test" : null].filter(
                (x): x is string => x != null,
            )
            return `${AppNames[appId]}: ${parts.join(", ")}`
        })
        .join("; ")
}

export type AppEnvsState = Record<number, AppLinkEnvs>

export const appLinksToEnvsState = (links: AvailableApp[] | undefined): AppEnvsState => {
    const byApp = groupAppLinksByEnv(links)
    const state: AppEnvsState = {}
    for (const id of ADMIN_CONFIGURABLE_APP_IDS) {
        state[id] = byApp.get(id) ?? {prod: false, test: false}
    }
    return state
}

export const envsStateToAppLinks = (state: AppEnvsState): AvailableApp[] => {
    const out: AvailableApp[] = []
    for (const id of ADMIN_CONFIGURABLE_APP_IDS) {
        const s = state[id]
        if (!s) {
            continue
        }
        if (s.prod) {
            out.push({appId: id, isTest: false})
        }
        if (s.test) {
            out.push({appId: id, isTest: true})
        }
    }
    return out
}

const CONFIGURABLE_APP_ID_SET = new Set<number>(ADMIN_CONFIGURABLE_APP_IDS)

/**
 * Builds the PUT body `appLinks`: editor state for known apps, plus any existing
 * links for appIds not listed in {@link ADMIN_CONFIGURABLE_APP_IDS} (unchanged).
 */
export function mergeAppLinksForSave(
    state: AppEnvsState,
    previousLinks: AvailableApp[] | undefined,
): AvailableApp[] {
    const fromEditor = envsStateToAppLinks(state)
    const preserved = (previousLinks ?? []).filter(
        (al) => !CONFIGURABLE_APP_ID_SET.has(al.appId),
    )
    return [...fromEditor, ...preserved]
}
